import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SortableTH, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState } from '@/components/data/EmptyState';
import { useToast } from '@/components/ui/toast';
import { useSeasonCompleteness, useSeasonCompletenessLevels } from '@/hooks/useCompleteness';
import { useTableSort } from '@/lib/useTableSort';
import { runSeasonCompletenessAudit, HOST_API_CONFIGURED } from '@/lib/hostApi';
import { cn, formatNumber, formatRelative } from '@/lib/utils';
import type { SeasonCompletenessLevelRow, SeasonCompletenessRow } from '@/types/harvest';

export function CompletenessRoute() {
  const completeness = useSeasonCompleteness();
  const levels = useSeasonCompletenessLevels();
  const queryClient = useQueryClient();
  const toast = useToast();

  const runAudit = useMutation({
    mutationFn: runSeasonCompletenessAudit,
    onSuccess: (result) => {
      toast.push(
        'success',
        `Completeness audit enqueued (${result.enqueued.genius} + ${result.enqueued.presto}). Results refresh once the jobs finish.`,
      );
      // Refetch the snapshot; the new audit lands a moment after the jobs run,
      // so this picks up the prior snapshot now and the fresh one on next poll.
      queryClient.invalidateQueries({ queryKey: ['season-completeness'] });
      queryClient.invalidateQueries({ queryKey: ['season-completeness-levels'] });
    },
    onError: (e) => {
      toast.push('error', e instanceof Error ? e.message : 'Failed to enqueue completeness audit');
    },
  });

  const rows = completeness.data ?? [];
  const blockersBySeason = useMemo(() => {
    const grouped = new Map<string, SeasonCompletenessLevelRow[]>();
    for (const level of levels.data ?? []) {
      if (level.is_complete) continue;
      const key = completenessSeasonKey(level);
      const existing = grouped.get(key);
      if (existing) {
        existing.push(level);
      } else {
        grouped.set(key, [level]);
      }
    }
    for (const value of grouped.values()) {
      value.sort((a, b) => a.level_order - b.level_order || a.level.localeCompare(b.level));
    }
    return grouped;
  }, [levels.data]);

  // Incomplete-first is the default ordering from the view; feeding rows in
  // with no initial key preserves it on load. Status sorts by the is_complete
  // flag, Completeness % by the numeric pct (nulls last).
  const sort = useTableSort(rows, {
    accessors: {
      is_complete: (r) => (r.is_complete ? 1 : 0),
      completeness_pct: (r) => (r.completeness_pct == null ? null : r.completeness_pct),
      // Season column renders `season_label ?? season_key`; sort by the same.
      season_label: (r) => r.season_label ?? r.season_key,
    },
  });

  return (
    <>
      <PageHeader
        title="Completeness"
        description="Per-season harvest + sync closure (from harvest.v_season_completeness_latest). Incomplete seasons sort to the top."
        actions={
          <div className="flex flex-col items-end gap-1">
            <Link to="/pbp/presto" className="text-sm text-primary hover:underline focus-visible:outline">Presto play-by-play coverage →</Link>
            <Button
              onClick={() => runAudit.mutate()}
              disabled={!HOST_API_CONFIGURED || runAudit.isPending}
              title={HOST_API_CONFIGURED ? undefined : 'Set VITE_HOST_API_URL to enable running the audit'}
            >
              <RefreshCw className={cn('h-4 w-4', runAudit.isPending && 'animate-spin')} />
              {runAudit.isPending ? 'Enqueuing…' : 'Run audit now'}
            </Button>
            <span className="text-[11px] text-muted-foreground">
              Recomputes the completeness snapshot (runs the genius + presto audit jobs).
            </span>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Season completeness</CardTitle>
          <CardDescription>
            Per-season harvest + sync closure. Incomplete seasons sort to the top.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {completeness.isError ? (
            <ErrorState error={completeness.error} />
          ) : completeness.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : !rows.length ? (
            <EmptyState
              icon={CheckCircle2}
              title="No season completeness yet"
              description="harvest.v_season_completeness_latest is empty. Once a completeness audit runs, each season will appear here."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <SortableTH label="Provider" sortKey="provider" active={sort.sortKey === 'provider'} direction={sort.sortDir} onSort={sort.toggle} />
                  <SortableTH label="Sport" sortKey="sport_key" active={sort.sortKey === 'sport_key'} direction={sort.sortDir} onSort={sort.toggle} />
                  <SortableTH label="Season" sortKey="season_label" active={sort.sortKey === 'season_label'} direction={sort.sortDir} onSort={sort.toggle} />
                  <SortableTH label="Completeness" sortKey="completeness_pct" active={sort.sortKey === 'completeness_pct'} direction={sort.sortDir} onSort={sort.toggle} />
                  <SortableTH label="Status" sortKey="is_complete" active={sort.sortKey === 'is_complete'} direction={sort.sortDir} onSort={sort.toggle} />
                  <SortableTH label="First failing level" sortKey="first_failing_level" active={sort.sortKey === 'first_failing_level'} direction={sort.sortDir} onSort={sort.toggle} />
                  <TH>Blocking levels</TH>
                  <SortableTH label="Missing" sortKey="missing_total" active={sort.sortKey === 'missing_total'} direction={sort.sortDir} onSort={sort.toggle} align="right" className="text-right" />
                  <SortableTH label="Stale" sortKey="stale_total" active={sort.sortKey === 'stale_total'} direction={sort.sortDir} onSort={sort.toggle} align="right" className="text-right" />
                  <SortableTH label="Excluded" sortKey="suppressed_total" active={sort.sortKey === 'suppressed_total'} direction={sort.sortDir} onSort={sort.toggle} align="right" className="text-right" />
                  <SortableTH label="Checked" sortKey="checked_at" active={sort.sortKey === 'checked_at'} direction={sort.sortDir} onSort={sort.toggle} />
                </TR>
              </THead>
              <TBody>
                {sort.sorted.map((r) => {
                  const key = completenessSeasonKey(r);
                  const blockers = blockersBySeason.get(key) ?? [];
                  return (
                    <TR key={key}>
                      <TD className="capitalize">{r.provider}</TD>
                      <TD className="capitalize">{r.sport_key}</TD>
                      <TD>{r.season_label ?? r.season_key}</TD>
                      <TD>{r.completeness_pct != null ? `${r.completeness_pct}%` : '—'}</TD>
                      <TD>
                        <Badge variant={r.is_complete ? 'success' : 'warning'}>
                          {r.is_complete ? 'COMPLETE' : 'INCOMPLETE'}
                        </Badge>
                      </TD>
                      <TD className="font-mono text-xs">{r.first_failing_level ?? '—'}</TD>
                      <TD className="min-w-72 max-w-xl">
                        <LevelBlockers
                          blockers={blockers}
                          isComplete={r.is_complete}
                          isLoading={levels.isLoading}
                          isError={levels.isError}
                        />
                      </TD>
                      <TD className="text-right tabular-nums">{formatNumber(r.missing_total)}</TD>
                      <TD className="text-right tabular-nums">{formatNumber(r.stale_total)}</TD>
                      <TD
                        className={cn('text-right tabular-nums', !r.suppressed_total && 'text-muted-foreground')}
                        title={
                          r.suppressed_total
                            ? `excluded as non-entitled: ${formatNumber(r.suppressed_total)} units (evidence: v_presto_roster_org_grant_evidence, v_presto_event_roster_org_grant_evidence)`
                            : undefined
                        }
                      >
                        {formatNumber(r.suppressed_total)}
                      </TD>
                      <TD>{formatRelative(r.checked_at)}</TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function completenessSeasonKey(row: Pick<SeasonCompletenessRow, 'provider' | 'sport_key' | 'season_key'>): string {
  return `${row.provider}|${row.sport_key}|${row.season_key}`;
}

interface LevelBlockersProps {
  blockers: SeasonCompletenessLevelRow[];
  isComplete: boolean;
  isLoading: boolean;
  isError: boolean;
}

function LevelBlockers({ blockers, isComplete, isLoading, isError }: LevelBlockersProps) {
  if (isComplete) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (isLoading) {
    return <span className="text-xs text-muted-foreground">Loading level detail…</span>;
  }
  if (isError) {
    return <span className="text-xs text-destructive">Level detail unavailable</span>;
  }
  if (!blockers.length) {
    return <span className="text-xs text-muted-foreground">No failing level rows</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {blockers.map((level) => (
        <Badge
          key={level.level}
          variant="danger"
          className="max-w-full gap-1 normal-case tracking-normal"
          title={[
            `expected ${formatNumber(level.expected_count)}`,
            `present ${formatNumber(level.present_count)}`,
            `missing ${formatNumber(level.missing_count)}`,
            `stale ${formatNumber(level.stale_count)}`,
            `pending ${formatNumber(level.pending_count)}`,
            `excluded as non-entitled ${formatNumber(level.suppressed_count)}`,
          ].join(' · ')}
        >
          <span className="font-mono">{level.level}</span>
          <span className="tabular-nums">m {formatNumber(level.missing_count)}</span>
          {level.stale_count > 0 ? <span className="tabular-nums">s {formatNumber(level.stale_count)}</span> : null}
          {level.pending_count > 0 ? <span className="tabular-nums">p {formatNumber(level.pending_count)}</span> : null}
        </Badge>
      ))}
    </div>
  );
}
